import React, { useEffect, useMemo, useState } from 'react';
import {
  ApplyDate,
  FlexWrapper,
  LeaveContainer,
  LeaveFooter,
  LeaveHeader,
  LeaveLabel,
  Title
} from '../../theme/common_style';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Skeleton } from 'antd';
import { Button, Table } from 'antd';
import '../../theme/antCustomComponents.css';
import { LmsNextIcon } from '../../theme/SvgIcons';
import { GetLeaveApi, UpdateLeaveStatusApi } from '../../redux/lms/apiRoute';
import { toast } from 'react-toastify';
import {
  convertToLocalDate,
  convertToLocalTime,
  getCategory,
  getFullName,
  getSlot,
  getStatusTag
} from '../../utils/common_functions';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import DeclineLeaveModal from '../../components/Modal/DeclineLeaveModal';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import AvatarImage from '../../components/common/AvatarImage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { StickyBox } from '../../utils/style';
import { LeaveStatusEnum } from '../../utils/constant';
import { useTheme } from '@emotion/react';

const RequestDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [approveLoading, setApproveLoading] = useState(false);
  const [lms, setLms] = useState();
  const [reporting, setReporting] = useState();
  const [loading, setLoading] = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [declineModal, setDeclineModal] = useState(false);
  const id = location.state?.lms?.id;
  const theme = useTheme();

  const handleApproveText = useMemo(() => {
    const obj = {};
    if (lms?.leave_status === LeaveStatusEnum.DECLINED) {
      obj['name'] = lms?.approved_by;
      obj['status'] = 'Decline by';
    } else if (lms?.leave_status === LeaveStatusEnum.APPROVED) {
      obj['name'] = lms?.approved_by;
      obj['status'] = 'Approve by';
    } else {
      const name = getFullName(reporting?.first_name, reporting?.middle_name, reporting?.last_name);
      obj['name'] = name;
      obj['status'] = 'Approver';
    }
    return obj;
  }, [lms, reporting]);

  const columns = [
    {
      title: 'Emp Code',
      render: (_, record) => <span>{record?.employee?.emp_code}</span>
    },
    { title: 'Leave type', dataIndex: 'leave_type', key: 'leave_type' },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (text) => (text ? convertToLocalDate(text) : 'N/A')
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (text) => (text ? convertToLocalDate(text) : 'N/A')
    },
    {
      title: 'Leave Category',
      dataIndex: 'leave_category',
      key: 'leave_category',
      render: (leave_category) => {
        return (
          <FlexWrapper justify={'start'} gap={'6px'} wrap={'unset'} cursor="default">
            <span style={{ whiteSpace: 'nowrap' }}>{getCategory(leave_category) ?? 'N/A'}</span>
          </FlexWrapper>
        );
      }
    },
    {
      title: 'Leave Slot',
      dataIndex: 'leave_slot',
      key: 'leave_slot',
      render: (leave_slot) => {
        return (
          <FlexWrapper justify={'start'} gap={'6px'} wrap={'unset'} cursor="default">
            <span style={{ whiteSpace: 'nowrap' }}>{getSlot(leave_slot) ?? 'N/A'}</span>
          </FlexWrapper>
        );
      }
    },
    { title: 'No of Leaves', dataIndex: 'no_of_leaves', key: 'no_of_leaves' }
  ];

  const updateLeaveStatus = async (status) => {
    setApproveLoading(true);
    try {
      const res = await UpdateLeaveStatusApi({
        ['leave_id']: Number(id),
        ['leave_status']: status
      });
      if (res.statusCode === 200) {
        toast.success(res?.message);
        getDetails();
      } else {
        toast.warning(res?.message);
      }
    } catch (errorInfo) {
      if (errorInfo.errorFields.length > 0) {
        form.scrollToField(errorInfo.errorFields[0].name);
      }
    } finally {
      setApproveLoading(false);
      setApproveModal(false);
    }
  };

  const getDetails = async () => {
    setLoading(true);
    try {
      const res = await GetLeaveApi({
        ['leave_id']: Number(id)
      });
      if (res.statusCode === 200) {
        setLms({
          ...res?.data?.leave,
          ['emp_id']: res?.data?.leave?.employee?.id
        });
        setReporting(res?.data?.reportingDetails?.employee);
      } else {
        toast.warning(res?.message);
      }
    } catch (errorInfo) {
      if (errorInfo.errorFields.length > 0) {
        form.scrollToField(errorInfo.errorFields[0].name);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      navigate('/requests', { replace: true });
    } else {
      getDetails();
    }
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {approveModal && (
        <ConfirmationModal
          open={approveModal}
          onCancel={() => setApproveModal(false)}
          title={'Approve Leave'}
          onSubmit={() => updateLeaveStatus(LeaveStatusEnum?.APPROVED)}
          buttonName={'Approve'}
          description={'Are you sure you want to approve this Leave?'}
          iconBG={theme.primaryColor}
          icon={
            <CheckOutlined
              style={{ fontSize: '24px', width: '24px', height: '24px', color: 'white' }}
            />
          }
          loading={approveLoading}
        />
      )}
      {declineModal && (
        <DeclineLeaveModal
          open={declineModal}
          onCancel={() => setDeclineModal(false)}
          title={'Decline Leave'}
          id={Number(id)}
          getDetails={getDetails}
          buttonName={'Decline'}
          description={'Are you sure you want to decline this Leave?'}
          iconBG={'#FB4A49'}
          icon={
            <CloseOutlined
              style={{ fontSize: '24px', width: '24px', height: '24px', color: 'white' }}
            />
          }
        />
      )}
      <StickyBox>
        <FlexWrapper justify="space-between">
          <Breadcrumb
            items={[
              { title: <Link to="/requests">Leave Request</Link> },
              { title: lms?.employee?.first_name }
            ]}
          />
        </FlexWrapper>
      </StickyBox>
      {!loading ? (
        <LeaveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <LeaveHeader>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Title>{lms?.leave_type}</Title>
                <ApplyDate>
                  <span style={{ fontWeight: '700' }}>Apply Date: </span>
                  {convertToLocalTime(lms?.created_at)}
                </ApplyDate>
              </div>
              <div>{getStatusTag(lms?.leave_status)}</div>
            </LeaveHeader>

            <FlexWrapper justify="flex-start" gap="16px" cursor="default">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                <LeaveLabel>Submitter</LeaveLabel>
                <FlexWrapper
                  gap="10px"
                  align="center"
                  justify="space-between"
                  cursor="default"
                  style={{
                    borderRadius: '100px',
                    padding: '6px 10px',
                    backgroundColor: '#F3F6FC'
                  }}>
                  <AvatarImage
                    style={{
                      height: '32px',
                      width: '32px',
                      minWidth: '32px',
                      fontSize: '16px'
                    }}
                    image={`${
                      process.env.REACT_APP_S3_BASE_URL +
                      'employee/profileImg/' +
                      lms?.employee?.id +
                      '.jpg'
                    }`}
                    name={getFullName(
                      lms?.employee?.first_name,
                      lms?.employee?.middle_name,
                      lms?.employee?.last_name
                    )}
                  />
                  <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>
                      {getFullName(
                        lms?.employee?.first_name,
                        lms?.employee?.middle_name,
                        lms?.employee?.last_name
                      )}
                    </div>
                  </div>
                </FlexWrapper>
              </div>

              {handleApproveText?.name && (
                <>
                  <div style={{ marginTop: '30px' }}>
                    <LmsNextIcon />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}>
                    <LeaveLabel>{handleApproveText?.status}</LeaveLabel>
                    <FlexWrapper gap="10px" alignItems="center" cursor="default">
                      <FlexWrapper
                        gap="10px"
                        align="center"
                        cursor="default"
                        justify="space-between"
                        style={{
                          borderRadius: '100px',
                          padding: '6px 10px',
                          backgroundColor: '#F3F6FC'
                        }}>
                        <AvatarImage
                          style={{
                            height: '32px',
                            width: '32px',
                            minWidth: '32px',
                            fontSize: '16px'
                          }}
                          image={`${
                            process.env.REACT_APP_S3_BASE_URL +
                            'employee/profileImg/' +
                            reporting?.id +
                            '.jpg'
                          }`}
                          name={handleApproveText?.name}
                        />
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start'
                          }}>
                          <div style={{ fontSize: '12px', fontWeight: '600' }}>
                            {handleApproveText?.name}
                          </div>
                        </div>
                      </FlexWrapper>
                    </FlexWrapper>
                  </div>
                  {lms?.leave_status !== LeaveStatusEnum.PENDING && (
                    <>
                      <div style={{ marginTop: '30px' }}>
                        <LmsNextIcon />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '10px'
                        }}>
                        <LeaveLabel>
                          {lms?.leave_status == LeaveStatusEnum.DECLINED
                            ? 'Declined On'
                            : 'Approved On'}
                        </LeaveLabel>
                        <FlexWrapper gap="10px" alignItems="center" cursor="default">
                          <FlexWrapper
                            gap="10px"
                            align="center"
                            cursor="default"
                            justify="space-between"
                            style={{
                              borderRadius: '100px',
                              padding: '6px 10px',
                              backgroundColor: '#F3F6FC',
                              fontSize: '12px',
                              fontWeight: '600',
                              height: '44px'
                            }}>
                            {convertToLocalTime(lms?.updated_at)}
                          </FlexWrapper>
                        </FlexWrapper>
                      </div>
                    </>
                  )}
                </>
              )}
            </FlexWrapper>

            <FlexWrapper align="flex-start" direction="column" gap="10px" cursor="default">
              <div
                style={{
                  fontFamily: 'Plus Jakarta Sans',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'default'
                }}>
                Leave Info
              </div>
              {lms && (
                <Table
                  prefixCls="antCustomTable"
                  columns={columns}
                  dataSource={[lms]}
                  pagination={false}
                  style={{
                    width: '100%',
                    border: '1px solid #C8C8C8',
                    borderRadius: '12px',
                    cursor: 'default'
                  }}
                />
              )}
            </FlexWrapper>

            <FlexWrapper align="flex-start" direction="column" gap="10px" cursor="default">
              <div
                style={{
                  fontFamily: 'Plus Jakarta Sans',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'default'
                }}>
                Leave Reason
              </div>
              <ReactQuill
                value={lms?.reason}
                readOnly
                className="custom-quill"
                modules={{ toolbar: false }}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  width: '100%'
                }}
              />
            </FlexWrapper>

            {lms?.declined_reason && lms?.leave_status === LeaveStatusEnum.DECLINED && (
              <FlexWrapper align="flex-start" direction="column" gap="10px" cursor="default">
                <div
                  style={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'default'
                  }}>
                  Declined Reason
                </div>
                <ReactQuill
                  value={lms?.declined_reason}
                  readOnly
                  className="custom-quill"
                  modules={{ toolbar: false }}
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    width: '100%'
                  }}
                />
              </FlexWrapper>
            )}
          </div>
        </LeaveContainer>
      ) : (
        <LeaveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <LeaveHeader>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Skeleton.Input style={{ width: 120 }} active size="small" />
                <Skeleton.Input style={{ width: 100, marginTop: 8 }} active size="small" />
              </div>
              <Skeleton.Button style={{ width: 80 }} active size="small" />
            </LeaveHeader>

            <FlexWrapper justify="flex-start" gap="16px" cursor="default">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                <Skeleton.Input style={{ width: 80 }} active size="small" />
                <FlexWrapper
                  gap="10px"
                  align="center"
                  justify="space-between"
                  cursor="default"
                  style={{
                    borderRadius: '100px',
                    padding: '6px 10px',
                    backgroundColor: '#F3F6FC'
                  }}>
                  <Skeleton.Avatar size={32} shape="circle" active />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Skeleton.Input active size="small" />
                  </div>
                </FlexWrapper>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                <Skeleton.Input style={{ width: 80 }} active size="small" />
                <FlexWrapper
                  gap="10px"
                  align="center"
                  justify="space-between"
                  cursor="default"
                  style={{
                    borderRadius: '100px',
                    padding: '6px 10px',
                    backgroundColor: '#F3F6FC'
                  }}>
                  <Skeleton.Avatar size={32} shape="circle" active />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Skeleton.Input active size="small" />
                  </div>
                </FlexWrapper>
              </div>
            </FlexWrapper>

            {/* Leave Info Skeleton */}
            <FlexWrapper align="flex-start" direction="column" gap="10px" cursor="default">
              <Skeleton.Input active size="small" />
              <Skeleton active paragraph={{ rows: 3 }} title={false} />
            </FlexWrapper>

            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          </div>
        </LeaveContainer>
      )}
      {!loading && lms?.leave_status === LeaveStatusEnum?.PENDING && (
        <LeaveFooter style={{ marginTop: '24px' }}>
          <Button
            onClick={() => {
              setApproveModal(true);
            }}
            type="text"
            style={{
              width: '107px',
              height: '44px',
              padding: '10px 20px',
              borderRadius: '10px',
              color: 'white',
              backgroundColor: '#2BC106',
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: 'Plus Jakarta Sans'
            }}>
            Approve
          </Button>
          <Button
            onClick={() => {
              setDeclineModal(true);
            }}
            type="text"
            style={{
              width: '107px',
              height: '44px',
              padding: '10px 20px',
              borderRadius: '10px',
              color: 'white',
              backgroundColor: '#D82D2D',
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: 'Plus Jakarta Sans'
            }}>
            Decline
          </Button>
        </LeaveFooter>
      )}
    </div>
  );
};

export default RequestDetails;
